import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import { sessionManager, CookieConsent } from '@/lib/sessionManager';
import { fetchWithSchema } from '@/lib/api';
import { useAppSettings } from '@/contexts/AppSettingsContext';
import { buildAppScopedKey } from '@/lib/appIdentity';
import { getSchemaScopedStorageKey, getSchemaScopedQueryKey } from '@/lib/schemaStorage';
import type { ReferralSource } from '@/utils/referralUtils';

const CONSENT_VERSION = '1.0';

interface CookieConsentContextType {
  consent: CookieConsent;
  hasConsented: boolean;
  updateConsent: (consent: CookieConsent) => void;
  saveConsent: (consent: CookieConsent, referralSource?: ReferralSource | null) => void;
  resetConsent: () => void;
  canUseAnalytics: () => boolean;
  canUseMarketing: () => boolean;
  canUseFunctional: () => boolean;
  showBanner: boolean;
  setShowBanner: (show: boolean) => void;
}

const defaultConsent: CookieConsent = {
  essential: true,
  analytics: false,
  marketing: false,
  functional: false,
  timestamp: new Date().toISOString(),
  version: CONSENT_VERSION
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}

interface CookieConsentProviderProps {
  children: ReactNode;
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const { settings } = useAppSettings();
  const appName = settings?.app_name || null;
  const storageKey = useMemo(() => {
    const baseKey = buildAppScopedKey(appName, 'cookie_consent');
    return getSchemaScopedStorageKey(baseKey);
  }, [appName]);
  const [consent, setConsent] = useState<CookieConsent>(defaultConsent);
  const [hasConsented, setHasConsented] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    sessionManager.setNamespace(appName);
  }, [appName]);

  // Query pentru consimțământul utilizatorului logat
  const { data: userConsent } = useQuery({
    queryKey: getSchemaScopedQueryKey(['userCookieConsent', userData?.id]),
    queryFn: async () => {
      if (!userData?.id) return null;
      
      try {
        const response = await fetchWithSchema(`/api/users/${userData.id}/preferences`);
        if (response.ok) {
          const data = await response.json();
          return data.preferences?.cookieConsent || null;
        }
      } catch (error) {
        console.warn('Failed to fetch user cookie consent:', error);
      }
      return null;
    },
    enabled: !!userData?.id,
    staleTime: 5 * 60 * 1000, // 5 minute
  });

  // Mutation pentru salvarea consimțământului
  const saveConsentMutation = useMutation({
    mutationFn: async ({ consentData, referralSource }: { consentData: CookieConsent; referralSource?: ReferralSource | null }) => {
      // console.log('🍪 Mutation started with:', consentData, 'referralSource:', referralSource);
      
      // Only save referral if analytics or marketing consent is given (GDPR requirement)
      const canTrackReferral = consentData.analytics || consentData.marketing;
      const referralToSave = canTrackReferral ? referralSource : null;

      if (userData?.id) {
        // Salvează în baza de date pentru utilizatori logați
        try {
          /* console.log('🍪 Saving cookie consent for logged user:', {
            userId: userData.id,
            consentData: consentData,
            referralSource: referralToSave
          }); */

          const response = await fetchWithSchema(`/api/users/${userData.id}/preferences`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          });
          
          const currentData = await response.json();
          const preferences = currentData?.preferences || {};
          
          // console.log('🍪 Current user preferences:', preferences);
          
          const updatedPreferences = {
            ...preferences,
            cookieConsent: consentData
          };
          
          // Save referral source if consent allows tracking
          if (referralToSave) {
            updatedPreferences.referralSource = referralToSave;
            updatedPreferences.referralSourceDetectedAt = new Date().toISOString();
          }
          
          // console.log('🍪 Updated preferences to save:', updatedPreferences);
          
          await fetchWithSchema(`/api/users/${userData.id}/preferences`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              preferences: updatedPreferences
            })
          });

          // Also save referral source via dedicated endpoint if provided
          if (referralToSave) {
            try {
              await fetchWithSchema(`/api/users/${userData.id}/referral`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  referralSource: referralToSave
                })
              });
            } catch (refError) {
              console.warn('Failed to save referral source via dedicated endpoint:', refError);
              // Don't fail the whole operation if referral save fails
            }
          }
          
          // console.log('✅ Cookie consent saved successfully for user:', userData.id);
        } catch (error) {
          console.error('❌ Failed to save user consent:', error);
          throw error;
        }
      } else {
        // Pentru utilizatori anonimi: folosește SessionManager
        const sessionId = sessionManager.getSessionId();
        const consentWithSession = {
          ...consentData,
          sessionId
        };
        
        // Salvează în localStorage
        localStorage.setItem(storageKey, JSON.stringify(consentWithSession));
        
        // Salvează prin SessionManager (care trimite către server)
        // Include referral source if consent allows tracking
        await sessionManager.saveConsentForSession(consentWithSession, referralToSave);
      }
    },
    onSuccess: () => {
      // console.log('🍪 Mutation successful - hiding banner');
      setHasConsented(true);
      setShowBanner(false);
      // Activează/dezactivează scripturile în funcție de consimțământ
      updateScripts();
    },
    onError: (error) => {
      console.error('🍪 Mutation failed:', error);
    }
  });

  // Încarcă consimțământul la inițializare
  useEffect(() => {
    const loadConsent = async () => {
      if (userConsent) {
        // Utilizator logat - folosește datele din DB
        setConsent(userConsent);
        setHasConsented(true);
        setShowBanner(false);
        // console.log('✅ Cookie consent loaded from user preferences:', userConsent);
      } else {
        // Utilizator anonim - verifică localStorage și serverul
        const storedConsent = localStorage.getItem(storageKey);
        if (storedConsent) {
          try {
            const parsed = JSON.parse(storedConsent);
            if (parsed.version === CONSENT_VERSION) {
              setConsent(parsed);
              setHasConsented(true);
              setShowBanner(false);
              // console.log('✅ Cookie consent loaded from localStorage');
            }
          } catch (error) {
            console.warn('Failed to parse stored cookie consent:', error);
          }
        } else {
          // Încearcă să preia de pe server folosind Session ID
          try {
            const serverConsent = await sessionManager.getConsentForSession();
            if (serverConsent) {
              setConsent(serverConsent);
              setHasConsented(true);
              setShowBanner(false);
              // Salvează în localStorage pentru viitor
              localStorage.setItem(storageKey, JSON.stringify(serverConsent));
              // console.log('✅ Cookie consent loaded from server session');
            } else {
              // Nu există consimțământ - arată banner-ul
              setShowBanner(true);
              // console.log('❌ No cookie consent found - showing banner');
            }
          } catch (error) {
            console.warn('Failed to fetch consent from server:', error);
            // În caz de eroare, arată banner-ul
            setShowBanner(true);
          }
        }
      }
    };

    loadConsent();
  }, [userConsent, storageKey]);

  const updateConsent = (newConsent: CookieConsent) => {
    setConsent(newConsent);
  };

  const saveConsent = (consentToSave: CookieConsent, referralSource?: ReferralSource | null) => {
    // If no referral source provided, try to get it from sessionStorage
    let finalReferralSource = referralSource;
    if (!finalReferralSource) {
      try {
        const referralKey = getSchemaScopedStorageKey('pending_referral');
        const pendingReferral = sessionStorage.getItem(referralKey);
        if (pendingReferral) {
          const parsed = JSON.parse(pendingReferral);
          if (parsed.source) {
            finalReferralSource = parsed.source;
            console.log('🔗 [CookieConsent] Found pending referral in sessionStorage:', finalReferralSource);
          }
        }
      } catch (error) {
        console.warn('Failed to parse pending referral from sessionStorage:', error);
      }
    }
    
    // console.log('🍪 saveConsent called with:', consentToSave, 'referralSource:', finalReferralSource);
    const consentWithTimestamp = {
      ...consentToSave,
      timestamp: new Date().toISOString()
    };
    // console.log('🍪 Calling mutation with:', consentWithTimestamp);
    saveConsentMutation.mutate({ consentData: consentWithTimestamp, referralSource: finalReferralSource });
  };

  const resetConsent = () => {
    const executeReset = async () => {
      setConsent(defaultConsent);
      setHasConsented(false);
      setShowBanner(true);

      if (userData?.id) {
        try {
          const response = await fetchWithSchema(`/api/users/${userData.id}/preferences`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          });

          const currentData = await response.json();
          const preferences = { ...(currentData?.preferences || {}) };
          delete preferences.cookieConsent;

          await fetchWithSchema(`/api/users/${userData.id}/preferences`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ preferences })
          });
        } catch (error) {
          console.error('Failed to clear user cookie consent preferences:', error);
        }
      } else {
        localStorage.removeItem(storageKey);
        sessionManager.clearSession().catch((error) => {
          console.warn('Failed to clear anonymous session', error);
        });
      }
    };

    void executeReset();
  };

  // Funcții helper pentru verificarea permisiunilor
  const canUseAnalytics = () => consent.analytics && hasConsented;
  const canUseMarketing = () => consent.marketing && hasConsented;
  const canUseFunctional = () => consent.functional && hasConsented;

  // Funcție pentru activarea/dezactivarea scripturilor
  const updateScripts = () => {
    // Analytics scripts (ex: Google Analytics)
    if (canUseAnalytics()) {
      // Activează Google Analytics sau alte scripturi de analiză
      // console.log('Analytics cookies enabled');
    } else {
      // Dezactivează scripturile de analiză
      // console.log('Analytics cookies disabled');
    }

    // Marketing scripts (ex: Facebook Pixel, Google Ads)
    if (canUseMarketing()) {
      // Activează scripturile de marketing
      // console.log('Marketing cookies enabled');
    } else {
      // Dezactivează scripturile de marketing
      // console.log('Marketing cookies disabled');
    }

    // Functional scripts (ex: chat widgets, maps)
    if (canUseFunctional()) {
      // Activează scripturile funcționale
      // console.log('Functional cookies enabled');
    } else {
      // Dezactivează scripturile funcționale
      // console.log('Functional cookies disabled');
    }
  };

  // Actualizează scripturile când se schimbă consimțământul
  useEffect(() => {
    if (hasConsented) {
      updateScripts();
    }
  }, [consent, hasConsented]);

  const value: CookieConsentContextType = {
    consent,
    hasConsented,
    updateConsent,
    saveConsent,
    resetConsent,
    canUseAnalytics,
    canUseMarketing,
    canUseFunctional,
    showBanner,
    setShowBanner
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}
